import PropTypes from 'prop-types'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import ReactImageFallback from 'react-image-fallback'
import styled from 'styled-components'

import { user as getUser } from '@h2/common/selectors/user'
import userHub from '@h2/assets/svg/icons/userHub.svg'

const sizes = {
  tiny: '16px',
  small: '24px',
  medium: '32px',
  large: '48px',
  xlarge: '92px'
}
const Image = styled(ReactImageFallback)`
  border-radius: 50%;
  width: ${({ width, size }) => width || sizes[size] || '1em'};
  height: ${({ height, size }) => height || sizes[size] || '1em'};
`
/**
 * A GravatarIcon contains the users gravatar profile image or a provided default image.
 */
const GravatarIcon = ({
  src = dynamicValues.userProfile?.image,
  alt = ``,
  isForUserInSession = false,
  size,
  width,
  height,
  onClick = () => {},
  ...rest
}) => {
  const user = useSelector(getUser)
  const appliedSrc = useMemo(() => {
    if (isForUserInSession && user) {
      return user.image
    }

    return src
  }, [src, user, isForUserInSession])

  return (
    <Image
      src={appliedSrc}
      size={size}
      width={width}
      height={height}
      alt={alt}
      aria-hidden="true"
      aria-label="icon"
      fallbackImage={userHub}
      onClick={onClick}
      {...rest}
    />
  )
}

GravatarIcon.propTypes = {
  /** A url string used to set the image */
  src: PropTypes.string,
  /** Whether to read the `src` from the authenticated user in redux */
  isForUserInSession: PropTypes.bool,
  /** A string used to set the alt text */
  alt: PropTypes.string,
  /** Callback when icon is clicked */
  onClick: PropTypes.func,
  /** Determine the size of the component. One of: 'tiny', 'small', 'medium', 'large' */
  size: PropTypes.oneOf(['tiny', 'small', 'medium', 'large', 'xlarge']),
  /** Sets the height (overrides size prop) */
  height: PropTypes.string,
  /** Sets the width (overrides size prop) */
  width: PropTypes.string
}

export { GravatarIcon }
